import { useParams, useLocation, Link } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, Clock, FileText, Phone, Globe, Award, Loader2,
  ArrowRight, Users, Shield, Star, MapPin, Briefcase, GraduationCap,
  ChevronRight, AlertCircle, HelpCircle, BookOpen, Calculator
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConsultationForm from '@/components/common/ConsultationForm';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Country configuration with flags, colors, and metadata
const COUNTRY_CONFIG = {
  'canada': {
    flag: '🇨🇦',
    name: 'Canada',
    color: '#EF4444',
    heroImage: 'https://images.unsplash.com/photo-1518235506717-e1ed3306a89b?w=1920&q=80&fm=webp',
    processingTime: '6-8 months',
    successRate: '98%',
    visaTypes: ['Express Entry', 'PNP', 'Family Sponsorship']
  },
  'australia': {
    flag: '🇦🇺',
    name: 'Australia',
    color: '#2563EB',
    heroImage: 'https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=1920&q=80&fm=webp',
    processingTime: '8-12 months',
    successRate: '96%',
    visaTypes: ['Subclass 189', 'Subclass 190', 'Subclass 491']
  },
  'uk': {
    flag: '🇬🇧',
    name: 'United Kingdom',
    color: '#1E40AF',
    heroImage: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=80&fm=webp',
    processingTime: '3-8 weeks',
    successRate: '94%',
    visaTypes: ['Skilled Worker', 'Global Talent', 'Scale-up']
  },
  'germany': {
    flag: '🇩🇪',
    name: 'Germany',
    color: '#FBBF24',
    heroImage: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=1920&q=80&fm=webp',
    processingTime: '4-8 weeks',
    successRate: '92%',
    visaTypes: ['Opportunity Card', 'EU Blue Card', 'Job Seeker']
  },
  'uae': {
    flag: '🇦🇪',
    name: 'UAE',
    color: '#10B981',
    heroImage: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80&fm=webp',
    processingTime: '2-4 weeks',
    successRate: '95%',
    visaTypes: ['Golden Visa', 'Work Visa', 'Investor Visa']
  },
  'usa': {
    flag: '🇺🇸',
    name: 'United States',
    color: '#3B82F6',
    heroImage: 'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=1920&q=80&fm=webp',
    processingTime: '12-24 months',
    successRate: '90%',
    visaTypes: ['EB-5', 'H-1B', 'Green Card']
  },
  'default': {
    flag: '🌍',
    name: 'International',
    color: '#C5A028',
    heroImage: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80&fm=webp',
    processingTime: '6-12 months',
    successRate: '95%',
    visaTypes: ['Work Visa', 'PR Visa', 'Study Visa']
  }
};

// Parse content into structured sections
const parseContentSections = (content) => {
  if (!content) return { overview: '', sections: [], faqs: [] };
  
  const lines = content.split('\n');
  const sections = [];
  let currentSection = null;
  let overview = '';
  let faqs = [];
  let inFAQ = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Detect H2 headers (## Header)
    if (line.startsWith('## ')) {
      if (currentSection) {
        sections.push(currentSection);
      }
      const title = line.replace('## ', '').trim();
      inFAQ = title.toLowerCase().includes('faq') || title.toLowerCase().includes('question');
      currentSection = {
        title,
        content: '',
        type: inFAQ ? 'faq' : 'standard'
      };
    } else if (currentSection) {
      currentSection.content += line + '\n';
    } else {
      // Content before first section is overview
      overview += line + '\n';
    }
  }
  
  if (currentSection) {
    sections.push(currentSection);
  }
  
  return { overview: overview.trim(), sections };
};

// Get icon for section based on title
const getSectionIcon = (title) => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes('eligib') || titleLower.includes('requirement')) return CheckCircle;
  if (titleLower.includes('process') || titleLower.includes('step')) return FileText;
  if (titleLower.includes('benefit') || titleLower.includes('advantage')) return Star;
  if (titleLower.includes('cost') || titleLower.includes('fee') || titleLower.includes('invest')) return Calculator;
  if (titleLower.includes('document')) return BookOpen;
  if (titleLower.includes('faq') || titleLower.includes('question')) return HelpCircle;
  if (titleLower.includes('time') || titleLower.includes('duration')) return Clock;
  if (titleLower.includes('job') || titleLower.includes('work') || titleLower.includes('employ')) return Briefcase;
  if (titleLower.includes('point') || titleLower.includes('score') || titleLower.includes('crs')) return Calculator;
  return Globe;
};

const VisaCMSPage = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  
  // Extract slug from URL if not provided via params (for explicit routes)
  const slug = paramSlug || location.pathname.split('/').pop();
  
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/pages/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPageData(data);

          // Title: never double-brand. If CMS already has " | " or "Zafco", keep as-is.
          const rawTitle = data.meta_title || data.title || '';
          const alreadyBranded =
            rawTitle.includes(' | ') ||
            rawTitle.toLowerCase().includes('zafco');
          document.title = alreadyBranded ? rawTitle : `${rawTitle} | Zafco HR`;

          // Add meta description (create the tag if missing)
          let metaDesc = document.querySelector('meta[name="description"]');
          if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
          }
          metaDesc.setAttribute('content', data.meta_description || '');
        } else if (response.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
      } catch (err) {
        setError('Connection error');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPage();
      window.scrollTo(0, 0);
    }

    return () => {
      // Restore canonical homepage title when leaving CMS page
      document.title =
        'Best Overseas Recruitment Agency in Delhi | MEA Approved | Zafco HR';
    };
  }, [slug]);

  const parsedContent = useMemo(() => {
    return parseContentSections(pageData?.content);
  }, [pageData?.content]);

  const countryConfig = useMemo(() => {
    const category = pageData?.category || 'default';
    return COUNTRY_CONFIG[category] || COUNTRY_CONFIG['default'];
  }, [pageData?.category]);

  const openWhatsApp = () => {
    const message = `Hello Zafco, I need help with ${pageData?.title || 'overseas recruitment'}`;
    window.open(`https://wa.me/919958848998?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="visa-page-loading">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-slate-800 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-[#C5A028] rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-slate-400 text-lg">Loading visa information...</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="visa-page-error">
        <div className="text-center max-w-md px-6">
          <AlertCircle className="w-16 h-16 text-[#C5A028] mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4 font-syne">Page Not Found</h1>
          <p className="text-slate-400 mb-8">The visa information you&apos;re looking for doesn&apos;t exist or has been moved.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Go Home
              </Button>
            </Link>
            <Button onClick={openWhatsApp} className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold">
              <Phone className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-950 min-h-screen" data-testid="visa-cms-page">
      
      {/* ============================================================ */}
      {/* HERO SECTION - Above the fold, immediate value */}
      {/* ============================================================ */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src={countryConfig.heroImage}
            alt={pageData.title}
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/50" />
        </div>

        {/* Hero Content */}
        <div className="container max-w-7xl mx-auto px-6 md:px-12 relative z-10 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Main Content */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              {/* Breadcrumb */}
              <motion.nav variants={fadeInUp} className="flex items-center gap-2 text-sm mb-6">
                <Link to="/" className="text-slate-400 hover:text-white transition-colors">Home</Link>
                <ChevronRight className="w-4 h-4 text-slate-400" />
                <Link to="/why-zafco" className="text-slate-400 hover:text-white transition-colors">Services</Link>
                <ChevronRight className="w-4 h-4 text-slate-400" />
                <span className="text-[#C5A028]">{countryConfig.name}</span>
              </motion.nav>

              {/* Country Badge */}
              <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6">
                <span className="text-4xl">{countryConfig.flag}</span>
                <span className="px-4 py-1.5 bg-[#C5A028]/10 border border-[#C5A028]/30 rounded-full text-[#C5A028] text-sm font-medium">
                  {countryConfig.name} Immigration
                </span>
              </motion.div>

              {/* Title */}
              <motion.h1 
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-syne leading-tight mb-6"
              >
                {pageData.title}
              </motion.h1>

              {/* Subtitle/Description */}
              <motion.p 
                variants={fadeInUp}
                className="text-xl text-slate-300 mb-8 leading-relaxed max-w-xl"
              >
                {pageData.meta_description || 'Expert guidance for your immigration journey with Zafco Human Resource Management.'}
              </motion.p>

              {/* Quick Stats */}
              <motion.div 
                variants={fadeInUp}
                className="grid grid-cols-3 gap-4 mb-8"
              >
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                  <Clock className="w-5 h-5 text-[#C5A028] mx-auto mb-2" />
                  <p className="text-white font-bold font-syne">{countryConfig.processingTime}</p>
                  <p className="text-xs text-slate-500">Processing</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                  <Award className="w-5 h-5 text-[#C5A028] mx-auto mb-2" />
                  <p className="text-white font-bold font-syne">{countryConfig.successRate}</p>
                  <p className="text-xs text-slate-500">Success Rate</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                  <Users className="w-5 h-5 text-[#C5A028] mx-auto mb-2" />
                  <p className="text-white font-bold font-syne">10,000+</p>
                  <p className="text-xs text-slate-500">Placements</p>
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <Button 
                  onClick={openWhatsApp}
                  size="lg"
                  className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-8 py-6 rounded-full btn-gold-glow"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Get Free Assessment
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-white/20 text-white hover:bg-white/10 px-8 py-6 rounded-full"
                  onClick={() => document.getElementById('content-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Learn More
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </motion.div>

            {/* Right: Consultation Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="hidden lg:block"
            >
              <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#C5A028]/20 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#C5A028]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white font-syne">Free Assessment</h3>
                    <p className="text-sm text-slate-400">Get expert guidance today</p>
                  </div>
                </div>
                <ConsultationForm variant="dark" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* TRUST INDICATORS BAR */}
      {/* ============================================================ */}
      <section className="py-6 bg-slate-900/50 border-y border-white/5">
        <div className="container max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#C5A028]" />
              <div>
                <p className="text-white font-semibold text-sm">Government Approved</p>
                <p className="text-xs text-slate-400">MEA Licensed Agency</p>
              </div>
            </div>
            <div className="h-8 w-px bg-white/10 hidden md:block" />
            <div className="flex items-center gap-3">
              <Star className="w-6 h-6 text-[#C5A028]" />
              <div>
                <p className="text-white font-semibold text-sm">15+ Years Experience</p>
                <p className="text-xs text-slate-500">Since 2009</p>
              </div>
            </div>
            <div className="h-8 w-px bg-white/10 hidden md:block" />
            <div className="flex items-center gap-3">
              <Globe className="w-6 h-6 text-[#C5A028]" />
              <div>
                <p className="text-white font-semibold text-sm">50+ Countries</p>
                <p className="text-xs text-slate-500">Global Network</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* MAIN CONTENT SECTION */}
      {/* ============================================================ */}
      <section id="content-section" className="py-20 bg-slate-950">
        <div className="container max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            
            {/* Left Sidebar - Table of Contents (Desktop) */}
            <motion.aside
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
              className="hidden lg:block lg:col-span-3"
            >
              <div className="sticky top-24">
                <h4 className="text-sm font-semibold text-[#C5A028] uppercase tracking-wider mb-4">
                  On This Page
                </h4>
                <nav className="space-y-1">
                  {parsedContent.sections.map((section, index) => {
                    const Icon = getSectionIcon(section.title);
                    return (
                      <button
                        key={section.title}
                        onClick={() => {
                          setActiveSection(index);
                          document.getElementById(`section-${index}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left text-sm transition-all ${
                          activeSection === index 
                            ? 'bg-[#C5A028]/10 text-[#C5A028] border-l-2 border-[#C5A028]' 
                            : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="line-clamp-1">{section.title}</span>
                      </button>
                    );
                  })}
                </nav>

                {/* Quick Contact Card */}
                <div className="mt-8 p-6 bg-gradient-to-br from-[#C5A028]/20 to-[#C5A028]/5 border border-[#C5A028]/20 rounded-2xl">
                  <h5 className="text-white font-bold mb-2">Need Help?</h5>
                  <p className="text-sm text-slate-400 mb-4">Speak with our experts for personalized guidance.</p>
                  <Button 
                    onClick={openWhatsApp}
                    size="sm"
                    className="w-full bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    WhatsApp Us
                  </Button>
                </div>
              </div>
            </motion.aside>

            {/* Main Content Area */}
            <motion.main
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="lg:col-span-9"
            >
              {/* Overview Section */}
              {parsedContent.overview && (
                <motion.div 
                  variants={fadeInUp}
                  className="mb-12 p-6 md:p-8 bg-gradient-to-r from-slate-900/80 to-slate-900/40 border border-white/5 rounded-2xl"
                >
                  <div className="prose prose-invert prose-base md:prose-lg max-w-none
                    prose-headings:font-syne prose-headings:text-white
                    prose-h1:text-2xl md:prose-h1:text-3xl prose-h1:mb-4
                    prose-p:text-slate-300 prose-p:leading-relaxed
                    prose-strong:text-white
                    prose-a:text-[#C5A028]
                  ">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{parsedContent.overview}</ReactMarkdown>
                  </div>
                </motion.div>
              )}

              {/* Content Sections */}
              {parsedContent.sections.map((section, index) => {
                const Icon = getSectionIcon(section.title);
                return (
                  <motion.article
                    key={section.title}
                    id={`section-${index}`}
                    variants={fadeInUp}
                    className="mb-12 scroll-mt-24"
                    onViewportEnter={() => setActiveSection(index)}
                  >
                    {/* Section Header */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-[#C5A028]/10 border border-[#C5A028]/20 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#C5A028]" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white font-syne">
                        {section.title}
                      </h2>
                    </div>

                    {/* Section Content */}
                    <div className="pl-0 md:pl-16">
                      <div className="prose prose-invert prose-base md:prose-lg max-w-none
                        prose-headings:font-syne prose-headings:text-white prose-headings:font-bold
                        prose-h3:text-lg md:prose-h3:text-xl prose-h3:mb-3 prose-h3:mt-6
                        prose-h4:text-base md:prose-h4:text-lg prose-h4:mb-2 prose-h4:mt-4 prose-h4:text-slate-200
                        prose-p:text-slate-400 prose-p:leading-relaxed prose-p:mb-4
                        prose-ul:text-slate-400 prose-ul:my-4 prose-ul:space-y-2
                        prose-ol:text-slate-400 prose-ol:my-4 prose-ol:space-y-2
                        prose-li:mb-1 prose-li:text-sm md:prose-li:text-base
                        prose-strong:text-white
                        prose-a:text-[#C5A028] prose-a:no-underline hover:prose-a:underline
                        prose-blockquote:border-l-[#C5A028] prose-blockquote:bg-slate-900/50 prose-blockquote:py-3 prose-blockquote:px-4 md:prose-blockquote:py-4 md:prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-sm md:prose-blockquote:text-base
                        prose-code:bg-slate-800 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-[#C5A028] prose-code:text-sm
                      ">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          components={{
                            table: ({node, ...props}) => (
                              <div className="overflow-x-auto my-6 rounded-xl border border-white/10">
                                <table className="w-full min-w-[400px] border-collapse" {...props} />
                              </div>
                            ),
                            thead: ({node, ...props}) => (
                              <thead className="bg-slate-800/80" {...props} />
                            ),
                            th: ({node, ...props}) => (
                              <th className="text-white font-semibold p-3 md:p-4 text-left text-sm md:text-base border-b border-white/10" {...props} />
                            ),
                            td: ({node, ...props}) => (
                              <td className="p-3 md:p-4 border-b border-white/5 text-slate-300 text-sm md:text-base" {...props} />
                            ),
                            tr: ({node, ...props}) => (
                              <tr className="hover:bg-white/5 transition-colors" {...props} />
                            ),
                          }}
                        >
                          {section.content}
                        </ReactMarkdown>
                      </div>
                    </div>

                    {/* Section Divider */}
                    {index < parsedContent.sections.length - 1 && (
                      <div className="mt-12 border-b border-white/5" />
                    )}
                  </motion.article>
                );
              })}

              {/* Bottom Stats Cards */}
              <motion.div 
                variants={fadeInUp}
                className="grid md:grid-cols-3 gap-6 mt-16"
              >
                <div className="bg-slate-900/50 border border-white/5 rounded-2xl p-6 text-center hover:border-[#C5A028]/30 transition-colors">
                  <Clock className="w-8 h-8 text-[#C5A028] mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white font-syne mb-1">{countryConfig.processingTime}</p>
                  <p className="text-sm text-slate-400">Average Processing Time</p>
                </div>
                <div className="bg-slate-900/50 border border-white/5 rounded-2xl p-6 text-center hover:border-[#C5A028]/30 transition-colors">
                  <Award className="w-8 h-8 text-[#C5A028] mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white font-syne mb-1">{countryConfig.successRate}</p>
                  <p className="text-sm text-slate-400">Success Rate</p>
                </div>
                <div className="bg-slate-900/50 border border-white/5 rounded-2xl p-6 text-center hover:border-[#C5A028]/30 transition-colors">
                  <Shield className="w-8 h-8 text-[#C5A028] mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white font-syne mb-1">15+ Years</p>
                  <p className="text-sm text-slate-400">Industry Experience</p>
                </div>
              </motion.div>
            </motion.main>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* MOBILE CONSULTATION FORM */}
      {/* ============================================================ */}
      <section className="py-16 bg-slate-900/30 lg:hidden">
        <div className="container max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#C5A028]/20 flex items-center justify-center">
                <Phone className="w-6 h-6 text-[#C5A028]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white font-syne">Free Assessment</h3>
                <p className="text-sm text-slate-400">Get expert guidance today</p>
              </div>
            </div>
            <ConsultationForm variant="dark" />
          </motion.div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* FINAL CTA SECTION */}
      {/* ============================================================ */}
      <section className="py-24 bg-slate-950 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        
        <div className="container max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.span variants={fadeInUp} className="text-6xl mb-6 block">
              {countryConfig.flag}
            </motion.span>
            <motion.h2 
              variants={fadeInUp} 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-syne mb-6"
            >
              Ready to Start Your {countryConfig.name} Journey?
            </motion.h2>
            <motion.p 
              variants={fadeInUp} 
              className="text-xl text-slate-400 mb-10"
            >
              Get your free assessment and personalized guidance from Zafco&apos;s immigration experts. 
              We&apos;ve helped 10,000+ professionals achieve their overseas dreams.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={openWhatsApp}
                size="lg"
                className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-10 py-7 text-lg rounded-full btn-gold-glow"
              >
                <Phone className="w-6 h-6 mr-3" />
                Get Free Assessment
              </Button>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-white/20 text-white hover:bg-white/10 px-10 py-7 text-lg rounded-full"
                >
                  Contact Us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Trust Badges */}
            <motion.div 
              variants={fadeInUp}
              className="mt-12 pt-8 border-t border-white/10 flex flex-wrap justify-center gap-8"
            >
              <div className="text-center">
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Phone</p>
                <p className="text-white font-semibold">+91-9958848998</p>
              </div>
              <div className="text-center">
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Email</p>
                <p className="text-white font-semibold">info@zafco.in</p>
              </div>
              <div className="text-center">
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">MEA Reg.</p>
                <p className="text-[#C5A028] font-mono text-sm">B-0762/DEL/PER/1000+/5/9148/2015</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default VisaCMSPage;
